// assets/AllCourses/Exercises/MultipleChoiceExercise.jsx

import React, { useEffect, useRef } from "react";
import useExerciseEngine from "@/hooks/useExerciseEngine";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MultipleChoiceExercise({
  exercise = {},
  userAnswer = {}, 
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.multipleChoiceQuestions)
    ? exercise.multipleChoiceQuestions
    : [];

  /* ================= ENGINE ================= */
 const {
  answers,
  setAnswer,
  submitIfReady,
  getOptionState,
} = useExerciseEngine({
  questions: questions.map((q) => ({
    ...q,
    type: "single",
  })),
  showResult,
  onComplete,
  externalAnswers: userAnswer || {},
  passPercent: 30,
});



  /* ================= DEBOUNCE (EDIT MODE) ================= */
  const debounceRef = useRef();

  const safeUpdateQuestions = (next) => {
    if (!isEditMode) return;

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      onUpdate("multipleChoiceQuestions", next);
    }, 300);
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);


 
  /* ================= EDIT MODE ================= */
  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];

    const current = next[idx] || {
      question: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };

    next[idx] = { ...current, [field]: value };

    safeUpdateQuestions(next);
  };

  /* ================= HANDLE CHANGE ================= */
const handleChange = (qIdx, value) => {
  if (isEditMode || showResult) return;

  setAnswer(qIdx, value);

  submitIfReady();
};

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6">
            <div className="space-y-6">
              <TextareaField
                label={`Question ${qIdx + 1}`}
                value={q.question || ""}
                onChange={(v) =>
                  updateQuestionField(qIdx, "question", v)
                }
                rows={3}
                required
              />

              {/* OPTIONS */}
              <div className="space-y-3">
                <Label>Options</Label>

                {(q.options || []).map((opt, optIdx) => (
                  <div key={optIdx} className="flex gap-3">
                    <StringField
                      value={opt}
                      onChange={(v) => {
                        const next = questions.map((qq, i) => {
                          if (i !== qIdx) return qq;

                          const updatedOptions = [...(qq.options || [])];
                          updatedOptions[optIdx] = v;

                          return {
                            ...qq,
                            options: updatedOptions,
                          };
                        });

                        safeUpdateQuestions(next);
                      }}
                    />

                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        safeUpdateQuestions(
                          questions.map((qq, i) =>
                            i === qIdx
                              ? {
                                  ...qq,
                                  options: qq.options.filter(
                                    (_, oi) => oi !== optIdx
                                  ),
                                }
                              : qq
                          )
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    safeUpdateQuestions(
                      questions.map((qq, i) =>
                        i === qIdx
                          ? {
                              ...qq,
                              options: [...(qq.options || []), ""],
                            }
                          : qq
                      )
                    )
                  }
                >
                  Add Option
                </Button>
              </div>

              <StringField
                label="Correct Answer"
                value={q.answer || ""}
                onChange={(v) =>
                  updateQuestionField(qIdx, "answer", v)
                }
                required
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestionField(
                    qIdx,
                    "requireCapitalization",
                    c
                  )
                }
              />
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            safeUpdateQuestions([
              ...questions,
              {
                question: "",
                options: [],
                answer: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Multiple Choice</CardTitle>
        <CardDescription>
          Choose the correct answer
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => {
          const selected = answers[qIdx];

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 space-y-4"
            >
              <p className="font-medium">{q.question}</p>

              <RadioGroup
                value={selected ?? ""}
                onValueChange={(v) => handleChange(qIdx, v)}
                disabled={showResult}
                className="space-y-3"
              >
                {(q.options || []).map((opt, optIdx) => {
                  const state = getOptionState(
                    selected,
                    opt,
                    q.answer
                  );

                  return (
                    <div
                      key={optIdx}
                      onClick={() => handleChange(qIdx, opt)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-4 transition cursor-pointer hover:bg-muted/60 active:scale-[0.98]",
                        state.className
                      )}
                    >
                      <RadioGroupItem
                        value={opt}
                        id={`mc-${qIdx}-${optIdx}`}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <Label
                        htmlFor={`mc-${qIdx}-${optIdx}`}
                        className="flex-1 cursor-pointer"
                      >
                        {opt}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}